/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AxiosError } from 'axios';
import { ErrorHandlerActions } from 'src/core/types/ErrorHandlerActions';

/**
 * A base factory function that will be used by other
 * handlers to compose their functionality.
 *
 * @param next The next handler to delegate to if this
 * handler fails to deal with the error.
 */
export const createBaseErrorHandler = () => {
    let next: ErrorHandlerActions;

    return {
        /**
         * Handles the error and returns a new Error object.
         * This particular instance is a dummy one and will
         * be overriden in other composed handlers.
         *
         * @param error The error received from the API.
         * @returns A formatted error object.
         */
        handle: async (error: AxiosError) => error,

        /**
         * Sets the given handler as the one to handle the
         * error in the case this handler fails to.
         *
         * @param handler The handler to handle the error if
         * this fails
         */
        setNext: (handler: ErrorHandlerActions): void => {
            next = handler;
        },

        /**
         * Delegates the error handling by calling the next
         * handler in the chain.
         */
        delegate: async (error: AxiosError) => {
            if (!next) {
                throw new Error('Could not handle error object.');
            }

            return next.handle(error);
        }
    };
};
